import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

// Round, icon-only button. The visible content is the `icon` child, so
// `aria-label` is required for accessibility.

type IconButtonSize = 'sm' | 'lg';
type IconButtonVariant = 'outline' | 'ghost';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  /** Required — the button has no visible text. */
  'aria-label': string;
  /**
   * `sm` (default): 32×32 — compact controls like month nav arrows or a
   * modal close button.
   * `lg`: 44×44 — pairs visually with a labeled `btn`-utility button (e.g.
   * the icon-only Edit button alongside a 'Sync now' text button).
   */
  size?: IconButtonSize;
  variant?: IconButtonVariant;
}

const SIZES: Record<IconButtonSize, string> = {
  sm: 'w-8 h-8',
  lg: 'w-11 h-11',
};

const VARIANTS: Record<IconButtonVariant, string> = {
  outline: 'border border-border-strong text-fg hover:bg-overlay',
  ghost: 'text-fg-tertiary hover:text-fg hover:bg-overlay',
};

export function IconButton({
  icon,
  size = 'sm',
  variant = 'outline',
  className,
  type,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type ?? 'button'}
      {...rest}
      className={clsx(
        'inline-flex items-center justify-center rounded-full transition-colors shrink-0',
        'disabled:opacity-40 disabled:hover:bg-transparent',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
    >
      {icon}
    </button>
  );
}
